import { Link } from '@tanstack/react-router';
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import { type ReactNode } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import { ANALYTICS_TIMEZONE } from '@apps/web/features/admin_agent_notes/model/constants';
import {
    CONFIDENCE_LABELS,
    DOMAIN_CHIP_CLASSES,
    DOMAIN_LABELS,
    itemReason,
    itemStatusChip,
} from '@apps/web/features/admin_agent_notes/ui/note_status';
import {
    destinationLink,
    destinationTypeLabel,
} from '@apps/web/lib/agent_note_destinations';
import { formatRelativeTime, formatTime } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';
import {
    type AdminAgentNoteAttemptRead,
    type AdminAgentNoteRead,
} from '@libraries/typescript/api_client';

type NoteItem = AdminAgentNoteRead['items'][number];
type ClientInitialTab = 'memory' | 'care-plan' | 'activity';

function deltaSeconds(from: string, to: string): string {
    const seconds = Math.max(
        0,
        (new Date(to).getTime() - new Date(from).getTime()) / 1000,
    );
    if (seconds < 120) {
        return `${seconds.toFixed(1)}s`;
    }
    return `${(seconds / 60).toFixed(1)}m`;
}

/** Wall-clock span from the first attempt's start to the last one's end. */
function attemptsDuration(
    attempts: ReadonlyArray<AdminAgentNoteAttemptRead>,
): string | null {
    const first = attempts[0];
    const last = attempts[attempts.length - 1];
    if (!first || !last?.completed_at) {
        return null;
    }
    return deltaSeconds(first.started_at, last.completed_at);
}

const ATTEMPT_STATUS_LABELS: Record<
    AdminAgentNoteAttemptRead['status'],
    string
> = {
    running: 'In progress',
    succeeded: 'Succeeded',
    failed: 'Failed',
};

const ATTEMPT_TRIGGER_LABELS: Record<
    AdminAgentNoteAttemptRead['trigger'],
    string
> = {
    initial: 'Initial delivery',
    automatic_retry: 'Automatic retry',
    pending_sweep: 'Pending sweep recovery',
    admin_retry: 'Admin retry',
    admin_reclassify: 'Admin reclassify',
    classification_handoff: 'Classification handoff',
    admin_release: 'Admin release',
};

function TraceButton({
    href,
    children,
}: {
    readonly href: string;
    readonly children: ReactNode;
}) {
    return (
        <Button
            variant="outline"
            size="sm"
            className="h-6 gap-1 px-2 text-xs"
            asChild={true}
        >
            <a href={href} target="_blank" rel="noreferrer">
                {children}
                <ArrowUpRight aria-hidden="true" className="h-3 w-3" />
            </a>
        </Button>
    );
}

/**
 * One attempt, rendered only inside an expanded history.
 *
 * Everything here — trigger, model, predecessor, trace links, the raw failure
 * string — is debugging detail, noise on every note that worked, so it stays
 * behind a disclosure.
 */
function AttemptRow({
    attempt,
    predecessor,
    predecessorIsMounted = false,
    stageLabel = 'Attempt',
}: {
    readonly attempt: AdminAgentNoteAttemptRead;
    readonly predecessor?: AdminAgentNoteAttemptRead;
    // A collapsed Collapsible unmounts its rows, so an anchor into another
    // disclosure resolves to nothing. Link only within the same list.
    readonly predecessorIsMounted?: boolean;
    readonly stageLabel?: string;
}) {
    return (
        <li
            id={`attempt-${attempt.id}`}
            className="border-border/60 flex flex-col gap-1.5 border-l py-1.5 pl-3"
        >
            <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-xs font-medium">
                    {stageLabel} {attempt.attempt_number}
                </span>
                <Badge
                    variant="outline"
                    className={cn(
                        'h-5 px-1.5 text-[10px] font-normal',
                        attempt.status === 'succeeded' &&
                            'border-emerald-500/30 text-emerald-700 dark:text-emerald-400',
                        attempt.status === 'failed' &&
                            'border-red-500/30 text-red-700 dark:text-red-400',
                    )}
                >
                    {ATTEMPT_STATUS_LABELS[attempt.status]}
                </Badge>
                <span className="text-muted-foreground text-[11px]">
                    {ATTEMPT_TRIGGER_LABELS[attempt.trigger]}
                </span>
                <span className="text-muted-foreground text-[11px]">
                    {formatTime(attempt.started_at, ANALYTICS_TIMEZONE)}
                    {attempt.completed_at &&
                        ` · ${deltaSeconds(attempt.started_at, attempt.completed_at)}`}
                </span>
                {attempt.model && (
                    <span className="text-muted-foreground font-mono text-[10px]">
                        {attempt.model}
                    </span>
                )}
            </span>
            {attempt.predecessor_attempt_id && (
                <p className="text-muted-foreground text-[11px]">
                    {predecessor && predecessorIsMounted ? (
                        <a
                            className="underline underline-offset-2"
                            href={`#attempt-${predecessor.id}`}
                        >
                            Follows {predecessor.stage.replace('_', ' ')}{' '}
                            attempt {predecessor.attempt_number}
                        </a>
                    ) : predecessor ? (
                        `Follows ${predecessor.stage.replace('_', ' ')} attempt ${predecessor.attempt_number}`
                    ) : (
                        'Follows an earlier attempt outside this view'
                    )}
                </p>
            )}
            {attempt.failure_reason && (
                <p className="text-muted-foreground font-mono text-[11px]">
                    {attempt.failure_reason}
                </p>
            )}
            {(attempt.logfire_trace_url || attempt.braintrust_url) && (
                <span className="flex flex-wrap gap-1.5">
                    {attempt.logfire_trace_url && (
                        <TraceButton href={attempt.logfire_trace_url}>
                            Logs
                        </TraceButton>
                    )}
                    {attempt.braintrust_url && (
                        <TraceButton href={attempt.braintrust_url}>
                            Braintrust
                        </TraceButton>
                    )}
                </span>
            )}
        </li>
    );
}

function DestinationAction({
    item,
    note,
    onOpenCaregiver,
    onOpenClient,
    onOpenPlaybook,
}: {
    readonly item: NoteItem;
    readonly note: AdminAgentNoteRead;
    readonly onOpenCaregiver: (caregiverId: string) => void;
    readonly onOpenClient: (
        clientId: string,
        initialTab?: ClientInitialTab,
    ) => void;
    readonly onOpenPlaybook: (playbookId: string) => void;
}) {
    const { result_ref_table: resultRefTable, result_ref_id: resultRefId } =
        item;
    if (!resultRefTable || !resultRefId) {
        return null;
    }
    const label = destinationTypeLabel(resultRefTable);
    if (resultRefTable.startsWith('agent_playbook')) {
        return (
            <Button
                variant="outline"
                size="sm"
                className="h-6 gap-1 px-2 text-xs"
                onClick={() => {
                    onOpenPlaybook(resultRefId);
                }}
            >
                {label}
            </Button>
        );
    }

    const link = destinationLink(resultRefTable, note, item);
    if (link?.to === '/caregivers') {
        return (
            <Button
                variant="outline"
                size="sm"
                className="h-6 gap-1 px-2 text-xs"
                onClick={() => {
                    onOpenCaregiver(link.search.caregiverId);
                }}
            >
                {label}
            </Button>
        );
    }
    if (link?.to === '/clients') {
        return (
            <Button
                variant="outline"
                size="sm"
                className="h-6 gap-1 px-2 text-xs"
                onClick={() => {
                    onOpenClient(
                        link.search.clientId,
                        resultRefTable.includes('memory')
                            ? 'memory'
                            : undefined,
                    );
                }}
            >
                {label}
            </Button>
        );
    }
    if (link) {
        return (
            <Button
                variant="outline"
                size="sm"
                className="h-6 gap-1 px-2 text-xs"
                asChild={true}
            >
                <Link {...link}>
                    {label}
                    <ArrowUpRight aria-hidden="true" className="h-3 w-3" />
                </Link>
            </Button>
        );
    }
    return (
        <Badge
            variant="outline"
            className="text-muted-foreground text-[10px] font-normal"
        >
            {label}
        </Badge>
    );
}

/**
 * One row per note item: what it said, where it ended up, why if it did not
 * land, and where to go look. The processing history collapses behind a
 * disclosure because it only matters when something went wrong.
 */
function ItemRow({
    item,
    note,
    attempts,
    attemptsById,
    legacyAttemptCount,
    attemptsLoaded,
    onOpenCaregiver,
    onOpenClient,
    onOpenPlaybook,
}: {
    readonly item: NoteItem;
    readonly note: AdminAgentNoteRead;
    readonly attempts: ReadonlyArray<AdminAgentNoteAttemptRead>;
    readonly attemptsById: Map<string, AdminAgentNoteAttemptRead>;
    readonly legacyAttemptCount: number;
    readonly attemptsLoaded: boolean;
    readonly onOpenCaregiver: (caregiverId: string) => void;
    readonly onOpenClient: (
        clientId: string,
        initialTab?: ClientInitialTab,
    ) => void;
    readonly onOpenPlaybook: (playbookId: string) => void;
}) {
    const chip = itemStatusChip(item);
    const reason = itemReason(item);
    const noteEntityNames = new Set(
        [note.caregiver?.name, note.client?.name, note.shift?.name].filter(
            Boolean,
        ),
    );
    const extraRefs = item.entity_refs.filter(
        (ref) => !noteEntityNames.has(ref.name),
    );
    const duration = attemptsDuration(attempts);
    const attemptCount = attempts.length || legacyAttemptCount;
    const historyLabel =
        attemptCount === 0
            ? null
            : `${attemptCount} ${attemptCount === 1 ? 'attempt' : 'attempts'}${
                  duration ? ` · ${duration}` : ''
              }`;

    return (
        <li className="border-border/60 flex flex-col gap-2 border-b py-3 last:border-b-0">
            <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5">
                <p className="min-w-0 flex-1 text-sm">
                    {item.extracted_content}
                </p>
                <span
                    className={cn(
                        'rounded px-1.5 py-0.5 text-[11px] whitespace-nowrap',
                        chip.className,
                    )}
                >
                    {chip.label}
                </span>
            </div>
            {reason && (
                <p className="text-muted-foreground text-xs">{reason}</p>
            )}
            <div className="flex flex-wrap items-center gap-1.5">
                <span
                    className={cn(
                        'rounded px-1.5 py-0.5 text-[11px]',
                        DOMAIN_CHIP_CLASSES[item.domain],
                    )}
                >
                    {DOMAIN_LABELS[item.domain]}
                </span>
                <span className="text-muted-foreground text-[11px]">
                    {CONFIDENCE_LABELS[item.confidence]}
                </span>
                {extraRefs.map((ref, index) => (
                    <Badge
                        key={`${ref.entity_type}-${ref.id_hint ?? index}`}
                        variant="outline"
                        className="text-[10px] font-normal"
                    >
                        {ref.name} · {ref.entity_type}
                    </Badge>
                ))}
                <DestinationAction
                    item={item}
                    note={note}
                    onOpenCaregiver={onOpenCaregiver}
                    onOpenClient={onOpenClient}
                    onOpenPlaybook={onOpenPlaybook}
                />
                {historyLabel &&
                    (attempts.length === 0 ? (
                        // Only once the detail fetch has landed: an item with
                        // durable history must never flash "unavailable".
                        attemptsLoaded && (
                            <span className="text-muted-foreground text-[11px]">
                                {historyLabel} · details unavailable
                            </span>
                        )
                    ) : (
                        <Collapsible className="w-full">
                            <CollapsibleTrigger className="group text-muted-foreground hover:text-foreground flex cursor-pointer items-center gap-1 text-[11px]">
                                <ChevronRight
                                    aria-hidden="true"
                                    className="h-3 w-3 transition-transform group-data-[state=open]:rotate-90"
                                />
                                {historyLabel}
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                                <ul className="mt-1.5 list-none">
                                    {attempts.map((attempt) => (
                                        <AttemptRow
                                            key={attempt.id}
                                            attempt={attempt}
                                            predecessor={
                                                attempt.predecessor_attempt_id
                                                    ? attemptsById.get(
                                                          attempt.predecessor_attempt_id,
                                                      )
                                                    : undefined
                                            }
                                            predecessorIsMounted={attempts.some(
                                                (sibling) =>
                                                    sibling.id ===
                                                    attempt.predecessor_attempt_id,
                                            )}
                                        />
                                    ))}
                                </ul>
                            </CollapsibleContent>
                        </Collapsible>
                    ))}
            </div>
        </li>
    );
}

export function NoteProcessingStepper({
    note,
    attemptsError = false,
    onOpenCaregiver,
    onOpenClient,
    onOpenPlaybook,
}: {
    readonly note: AdminAgentNoteRead;
    readonly attemptsError?: boolean;
    readonly onOpenCaregiver: (caregiverId: string) => void;
    readonly onOpenClient: (
        clientId: string,
        initialTab?: ClientInitialTab,
    ) => void;
    readonly onOpenPlaybook: (playbookId: string) => void;
}) {
    // While the detail fetch is in flight the list row carries attempts=null;
    // suppress the classification summary then so a note with durable history
    // never flashes "details unavailable".
    const attemptsLoaded = note.attempts !== null;
    const items = [...note.items].sort((a, b) => a.item_index - b.item_index);
    const attempts = [...(note.attempts ?? [])].sort(
        (a, b) =>
            new Date(a.started_at).getTime() -
                new Date(b.started_at).getTime() || a.id.localeCompare(b.id),
    );
    const attemptsById = new Map(
        attempts.map((attempt) => [attempt.id, attempt]),
    );
    const attemptsByItemId = new Map<
        string,
        Array<AdminAgentNoteAttemptRead>
    >();
    for (const attempt of attempts) {
        if (attempt.stage === 'item_processing' && attempt.item_id !== null) {
            const forItem = attemptsByItemId.get(attempt.item_id) ?? [];
            forItem.push(attempt);
            attemptsByItemId.set(attempt.item_id, forItem);
        }
    }
    const classificationAttempts = attempts.filter(
        (attempt) => attempt.stage === 'classification',
    );
    // Attempts whose item is gone from this view still deserve a home; they
    // are the only trace that a since-removed item was ever processed.
    const orphanAttempts = attempts.filter(
        (attempt) =>
            attempt.stage === 'item_processing' &&
            (attempt.item_id === null ||
                !items.some((item) => item.id === attempt.item_id)),
    );
    const classificationFailed = note.status === 'failed' && items.length === 0;

    return (
        <div className="flex flex-col gap-3">
            {!attemptsLoaded &&
                (attemptsError ? (
                    <p className="text-destructive text-xs" role="status">
                        Couldn’t load attempt history. Refresh to try again.
                    </p>
                ) : (
                    <p className="text-muted-foreground text-xs" role="status">
                        Loading attempt history…
                    </p>
                ))}
            {note.attempts_truncated && (
                <p className="rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs text-amber-800 dark:text-amber-300">
                    Older attempts are omitted from this view.
                </p>
            )}
            <p className="text-muted-foreground text-xs">
                Captured {formatTime(note.created_at, ANALYTICS_TIMEZONE)}
                {note.classified_at &&
                    ` · classified in ${deltaSeconds(note.created_at, note.classified_at)}`}
            </p>
            {classificationFailed && (
                <p className="rounded bg-red-500/10 px-2 py-1 text-xs text-red-700 dark:text-red-400">
                    Classification failed
                    {note.last_error ? `: ${note.last_error}` : '.'}
                </p>
            )}
            {/* Classification stays expanded: it is one step per note, not
                one per item, so it never contributed to the repetition the
                item rows were collapsed to fix. */}
            {classificationAttempts.length > 0 && (
                <ul className="list-none">
                    {classificationAttempts.map((attempt) => (
                        <AttemptRow
                            key={attempt.id}
                            attempt={attempt}
                            stageLabel="Classification attempt"
                            predecessor={
                                attempt.predecessor_attempt_id
                                    ? attemptsById.get(
                                          attempt.predecessor_attempt_id,
                                      )
                                    : undefined
                            }
                            predecessorIsMounted={classificationAttempts.some(
                                (sibling) =>
                                    sibling.id ===
                                    attempt.predecessor_attempt_id,
                            )}
                        />
                    ))}
                </ul>
            )}
            {attemptsLoaded &&
                classificationAttempts.length === 0 &&
                note.classify_attempts > 0 && (
                    <p className="text-muted-foreground text-xs">
                        {note.classify_attempts}{' '}
                        {note.classify_attempts === 1 ? 'attempt' : 'attempts'}{' '}
                        · details unavailable
                    </p>
                )}
            {items.length > 0 && (
                <ul className="list-none">
                    {items.map((item) => (
                        <ItemRow
                            key={item.id}
                            item={item}
                            note={note}
                            attempts={attemptsByItemId.get(item.id) ?? []}
                            attemptsById={attemptsById}
                            legacyAttemptCount={item.process_attempts}
                            attemptsLoaded={attemptsLoaded}
                            onOpenCaregiver={onOpenCaregiver}
                            onOpenClient={onOpenClient}
                            onOpenPlaybook={onOpenPlaybook}
                        />
                    ))}
                </ul>
            )}
            {orphanAttempts.length > 0 && (
                <Collapsible>
                    <CollapsibleTrigger className="group text-muted-foreground hover:text-foreground flex cursor-pointer items-center gap-1 text-[11px]">
                        <ChevronRight
                            aria-hidden="true"
                            className="h-3 w-3 transition-transform group-data-[state=open]:rotate-90"
                        />
                        {orphanAttempts.length} attempt
                        {orphanAttempts.length === 1 ? '' : 's'} for items no
                        longer on this note
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <ul className="mt-1.5 list-none">
                            {orphanAttempts.map((attempt) => (
                                <AttemptRow
                                    key={attempt.id}
                                    attempt={attempt}
                                    predecessor={
                                        attempt.predecessor_attempt_id
                                            ? attemptsById.get(
                                                  attempt.predecessor_attempt_id,
                                              )
                                            : undefined
                                    }
                                    predecessorIsMounted={orphanAttempts.some(
                                        (sibling) =>
                                            sibling.id ===
                                            attempt.predecessor_attempt_id,
                                    )}
                                />
                            ))}
                        </ul>
                    </CollapsibleContent>
                </Collapsible>
            )}
            {items.length === 0 && !classificationFailed && (
                <p className="text-muted-foreground text-xs">
                    {note.status === 'pending'
                        ? `Awaiting classification for ${formatRelativeTime(note.created_at).replace(' ago', '')}`
                        : 'No items were extracted from this note.'}
                </p>
            )}
        </div>
    );
}
